// connectApi -> creates parameters
// post -> creates fetch options
// abortableFetch -> creates a thenable object

import { Civet } from './civet';
import { linkPrefix } from '@js/utils/toolbox';

/**
 *
 * @param {string} target
 * @param {string} method
 * @param {string} parameters
 * @param {boolean} shouldRetry - Whether to retry on failure
 * @param {Object} retryConfig - Configuration object
 * @param {number} retryConfig.retries - Number of retry attempts (default: 0)
 * @param {number} retryConfig.retryDelay - Delay between retries in ms (default: 1000)
 * @returns {Promise}
 */
export function ConnectApi (target, method, parameters, shouldRetry = false, retryConfig = { retries: 3, retryDelay: 3000 }) {
	// const endpoint = lLink('conapi');
	const baseUrl = linkPrefix.connectApiBase ? linkPrefix.connectApiBase : window.location.origin;
	let languagePrefix = '';
	if (globalThis.js_params.language !== globalThis.js_params.defaultLanguage) {
		languagePrefix = `/${globalThis.js_params.language}`;
	}
	const url = `${baseUrl}${languagePrefix}/api/${target}/${method}`;
	if (!Array.isArray(parameters)) {
		console.warn('arguments must be an array', parameters);
		parameters = [parameters];
	}

	if (shouldRetry) {
		const { retries = 3, retryDelay = 3000 } = retryConfig;
		return post(url, parameters, retries, retryDelay);
	}
	return post(url, parameters);
}

if (typeof window !== 'undefined') {
	window.ConnectApi = ConnectApi;
}

/**
 *
 * @param {String} url
 * @param {Object} options
 * @param {String} method
 * @param {Number} retries
 * @param {Number} retryDelay
 * @returns
 */
export function networkRequest (url, options, method = 'POST', retries = 0, retryDelay = 1000) {
	const body = JSON.stringify(options);
	const ac = new AbortController();

	const attempt = left =>
		fetch(url, {
			method,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json'
			},
			...(method === 'GET' ? {} : { body }),
			signal: ac.signal
		})
			.then(res => unwrapResponseData(res, body, url))
			.catch((error) => {
				// if we aborted, stop immediately
				if (error.name === 'AbortError') {
					throw error;
				}
				// stop immediately
				if (left <= 0) {
					throw error;
				}
				// Append retry count to connectApiParameters for better error tracking
				if (error?.connectApiParameters) {
					error.connectApiParameters = JSON.parse(error.connectApiParameters);
					error.connectApiParameters.retriesLeft = left;
					error.connectApiParameters = JSON.stringify(error.connectApiParameters);
				}


				Civet.reportError(error);
				// bubble up
				console.log(`ConnectApi error, retrying ${left} more times`, error);
				return new Promise(r => setTimeout(r, retryDelay)).then(() => attempt(left - 1));
			});

	const promise = attempt(retries);
	// return a plain object so callers can optionally abort
	return { promise, abort: () => ac.abort() };
}


/**
 *
 * @param {string} url
 * @param {object|string} options
 * @param {number} retries
 * @param {number} retryDelay
 * @returns {Promise}
 */
function post (url, options, retries = 0, retryDelay = 1000) {
	return networkRequest(url, options, 'POST', retries, retryDelay).promise;
}

/**
 *
 * @param {string} url
 * @param {object|string} options
 * @param {number} retries
 * @param {number} retryDelay
 * @returns {Promise}
 */
export function get (url, options, retries = 0, retryDelay = 1000) {
	return networkRequest(url, options, 'GET', retries, retryDelay).promise;
}

/**
 *
 * @param {Response} response
 * @returns {string|object}
 */
function unwrapResponseData (response, connectApiParameters, url) {
	if (!response.ok) {
		const error = new Error(`ConnectApi fail: status - ${response?.statusText}, status code - ${response?.status}`);
		const headers = {};
		try {
			response.headers.forEach((value, key) => {
				headers[key] = value;
			});
		} catch (tryCatchError) {
			console.log('Error in headers.forEach', tryCatchError);
		}
		const headersLength = JSON.stringify(headers).length;
		error.connectApiParameters = JSON.stringify({ url, connectApiParameters, headersLength, headers });
		console.log(error.connectApiParameters);
		return Promise.reject(error);
	}


	const contentType = response.headers.has('content-type')
		? response.headers.get('content-type')
		: '';

	const TYPE_JSON = new RegExp('^application/(x-)?json', 'i');
	const TYPE_TEXT = new RegExp('^text/', 'i');
	if (contentType === null) {
		return Promise.resolve(null);
	} else if (TYPE_JSON.test(contentType)) {
		return response.json();
	} else if (TYPE_TEXT.test(contentType)) {
		return response.text();
	} else {
		return (response.blob());
	}
}
